/**
 * RxDB replication setup.
 *
 * Starts push/pull replication for each registered collection against the
 * /api/rxdb/:collection/{pull,push} backend endpoints.
 *
 * Call `ensureReplication(userId)` once after the RxDB database is available
 * for a user. Subsequent calls for the same user are no-ops.
 */
import { replicateRxCollection, type RxReplicationState } from 'rxdb/plugins/replication';
import { type RxCollection } from 'rxdb';
import { BehaviorSubject, Subject, type Observable } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { getSession } from '@/services/authStore';
import { getClientInstanceId } from '@/services/clientInstance';
import { syncConfigurationService } from '@/services/syncConfiguration';
import { backfillDiaryDayBody } from '@/services/diaryDayBackfill';
import {
  DIARY_DAYS_COLLECTION,
  type DiaryDayDocType,
  type DiaryDaysCheckpoint,
  RECIPES_COLLECTION,
  type RecipeDocType,
  type RecipesCheckpoint,
  USER_FOODS_COLLECTION,
  type UserFoodDocType,
  type UserFoodsCheckpoint,
  GOALS_HISTORY_COLLECTION,
  type GoalHistoryDocType,
  type GoalsHistoryCheckpoint,
  WEIGHT_HISTORY_COLLECTION,
  type WeightHistoryDocType,
  type WeightHistoryCheckpoint,
  CUSTOM_FOOD_MIGRATIONS_COLLECTION,
  type CustomFoodMigrationDocType,
  type CustomFoodMigrationsCheckpoint,
  SETTINGS_COLLECTION,
  type SettingDocType,
  type SettingsCheckpoint,
  MEALS_COLLECTION,
  type MealsConfigurationDocType,
  type MealsCheckpoint,
} from '@macro-tracker/models';
import { getDb, type AppDatabase } from './database';
import {
  clearDiaryOutboxEntry,
  getPendingDiaryOutbox,
} from './diaryOutbox';

// ============================================================================
// API helpers
// ============================================================================

const resolveBaseUrl = (): string => {
  if (Capacitor.isNativePlatform()) {
    const configured = import.meta.env.VITE_API_BASE_URL as string | undefined;
    if (configured && configured.length > 0) return configured;
    if (typeof window !== 'undefined') return window.location.origin;
  }
  return '';
};

const rxdbFetch = async <T>(
  path: string,
  body: unknown,
): Promise<T> => {
  const session = await getSession();
  const token = session?.token;
  const base = resolveBaseUrl();
  const res = await fetch(`${base}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Instance-Id': getClientInstanceId(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`[rxdb] replication request failed: ${res.status} ${path}`);
  }
  return res.json() as Promise<T>;
};

// ============================================================================
// Per-user replication tracking
// ============================================================================

/**
 * Minimal handle for an active replication state.
 * Structural interface so specific RxReplicationState<T, C> instances satisfy it.
 */
export interface ReplicationStateHandle {
  readonly active$: Observable<boolean>;
  readonly error$: Observable<Error>;
  cancel(): Promise<void>;
  isStopped(): boolean;
  reSync(): void;
  /**
   * Resolves once all local writes for this collection have reached the remote
   * and the pull side is idle.
   */
  awaitInSync?(): Promise<true>;
  /**
   * Resolves once the collection has completed its first successful pull.
   * Present on live `RxReplicationState` handles; optional so the diary
   * coordinator (which is not routing-critical) can omit it.
   * NOTE: only resolves after a *successful* pull — never resolves while
   * offline — so callers must bound any await with a timeout.
   */
  awaitInitialReplication?(): Promise<void>;
}

const started = new Set<string>();
const startPromises = new Map<string, Promise<void>>();
const pendingDiarySchedules = new Set<string>();

/** All active replication states per user, keyed by userId then collection name. */
const statesMap = new Map<string, Map<string, ReplicationStateHandle>>();

/** Return all active replication states for the given user. */
export function getReplicationStates(userId: string): ReplicationStateHandle[] {
  return Array.from(statesMap.get(userId)?.values() ?? []);
}

/** Trigger an immediate pull cycle on all running replications. */
export function reSyncAll(userId: string): void {
  const collMap = statesMap.get(userId);
  if (!collMap) return;
  for (const state of collMap.values()) {
    if (!state.isStopped()) state.reSync();
  }
}

const MANUAL_SYNC_TIMEOUT_MS = 30_000;

const withManualSyncTimeout = (
  collectionName: string,
  promise: Promise<unknown>,
): Promise<unknown> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => {
        reject(new Error(`[rxdb] manual sync timed out for ${collectionName}`));
      }, MANUAL_SYNC_TIMEOUT_MS);
    }),
  ]).finally(() => {
    if (timer) clearTimeout(timer);
  });
};

/** Trigger an immediate sync on every running replication and wait for idle. */
export async function reSyncAllAndWait(userId: string): Promise<void> {
  const collMap = statesMap.get(userId);
  if (!collMap) return;

  const pending: Promise<unknown>[] = [];
  for (const [collectionName, state] of collMap.entries()) {
    if (state.isStopped()) continue;
    state.reSync();
    if (state.awaitInSync) {
      pending.push(withManualSyncTimeout(collectionName, state.awaitInSync()));
    }
  }

  await Promise.all(pending);
}

/** Trigger a pull cycle on a specific collection. */
export function reSyncCollection(userId: string, collection: string): void {
  if (collection === DIARY_DAYS_COLLECTION) {
    flushDiaryReplication(userId);
    return;
  }
  const state = statesMap.get(userId)?.get(collection);
  if (state && !state.isStopped()) state.reSync();
}

/** Cancel all replications for the given user. */
export async function cancelAll(userId: string): Promise<void> {
  startPromises.delete(userId);
  const collMap = statesMap.get(userId);
  const handles = new Set(collMap?.values() ?? []);
  const diaryCoordinator = diaryCoordinators.get(userId);
  if (diaryCoordinator) handles.add(diaryCoordinator);
  await Promise.all(Array.from(handles).map((s) => s.cancel().catch(() => {})));
  statesMap.delete(userId);
  diaryCoordinators.delete(userId);
  pendingDiarySchedules.delete(userId);
}

const DIARY_DEBOUNCE_MS = 2_000;
const DIARY_MAX_WAIT_MS = 10_000;

class DiaryReplicationCoordinator implements ReplicationStateHandle {
  private readonly activeSubject = new BehaviorSubject(false);
  private readonly errorSubject = new Subject<Error>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private firstScheduledAt: number | null = null;
  private running: Promise<void> | null = null;
  private activeState: RxReplicationState<DiaryDayDocType, DiaryDaysCheckpoint> | null = null;
  private stopped = false;
  private runRequested = false;

  readonly active$ = this.activeSubject.asObservable();
  readonly error$ = this.errorSubject.asObservable();

  constructor(
    private readonly db: AppDatabase,
    private readonly userId: string,
  ) {}

  private reportError(error: unknown): void {
    this.errorSubject.next(
      error instanceof Error ? error : new Error(String(error)),
    );
  }

  private requestFlush(): void {
    void this.flush().catch((error) => this.reportError(error));
  }

  schedule(): void {
    if (this.stopped) return;
    const now = Date.now();
    this.firstScheduledAt ??= now;
    const maxRemaining = Math.max(
      0,
      DIARY_MAX_WAIT_MS - (now - this.firstScheduledAt),
    );
    const delay = Math.min(DIARY_DEBOUNCE_MS, maxRemaining);
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.requestFlush();
    }, delay);
  }

  reSync(): void {
    if (this.stopped) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.firstScheduledAt = null;
    this.requestFlush();
  }

  async flush(): Promise<void> {
    if (this.stopped) return;
    this.runRequested = true;
    if (this.running) {
      return this.running;
    }

    const running = this.drain();
    this.running = running;

    try {
      await running;
    } finally {
      if (this.running === running) {
        this.running = null;
      }
    }

    if (this.runRequested && !this.stopped) {
      await this.flush();
    }
  }

  private async drain(): Promise<void> {
    this.firstScheduledAt = null;
    this.activeSubject.next(true);
    try {
      while (this.runRequested && !this.stopped) {
        this.runRequested = false;
        await this.runOnce();
      }
    } finally {
      this.activeSubject.next(false);
    }
  }

  private async runOnce(): Promise<void> {
    const snapshot = await getPendingDiaryOutbox(this.userId);
    type Doc = DiaryDayDocType & { _deleted: boolean };
    const normalizePulledDiaryDay = (doc: Doc): Doc => ({
      ...doc,
      body: backfillDiaryDayBody(doc.body),
    });
    const state = replicateRxCollection<DiaryDayDocType, DiaryDaysCheckpoint>({
      collection: this.db[DIARY_DAYS_COLLECTION],
      replicationIdentifier: `rxdb-${this.userId}-${DIARY_DAYS_COLLECTION}`,
      live: false,
      // The tab that made the local write must be able to run the one-shot.
      // CAS and the shared RxDB storage make concurrent tab attempts safe.
      waitForLeadership: false,
      retryTime: 10_000,
      autoStart: true,
      pull: {
        batchSize: 100,
        modifier: normalizePulledDiaryDay,
        handler: async (checkpoint, batchSize) =>
          rxdbFetch<{ documents: Doc[]; checkpoint: DiaryDaysCheckpoint }>(
            `/api/rxdb/${DIARY_DAYS_COLLECTION}/pull`,
            { checkpoint, batchSize },
          ),
      },
      push: {
        batchSize: 50,
        handler: async (rows) =>
          rxdbFetch<Doc[]>(`/api/rxdb/${DIARY_DAYS_COLLECTION}/push`, rows),
      },
    });
    this.activeState = state;
    const errorSub = state.error$.subscribe((error) => {
      this.reportError(error);
    });

    try {
      await state.awaitInitialReplication();
      await Promise.all(
        snapshot.map((entry) =>
          clearDiaryOutboxEntry(
            this.userId,
            entry.id,
            entry.generation,
          ),
        ),
      );
    } finally {
      errorSub.unsubscribe();
      this.activeState = null;
    }
  }

  async cancel(): Promise<void> {
    this.stopped = true;
    this.runRequested = false;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    await this.activeState?.cancel().catch(() => {});
    await this.running?.catch(() => {});
    this.activeSubject.complete();
    this.errorSubject.complete();
  }

  isStopped(): boolean {
    return this.stopped;
  }

  async awaitInSync(): Promise<true> {
    await this.flush();
    return true;
  }
}

const diaryCoordinators = new Map<string, DiaryReplicationCoordinator>();

export function scheduleDiaryReplication(userId: string): void {
  const coordinator = diaryCoordinators.get(userId);
  if (coordinator) {
    coordinator.schedule();
  } else {
    pendingDiarySchedules.add(userId);
  }
}

export function flushDiaryReplication(userId: string): void {
  const coordinator = diaryCoordinators.get(userId);
  if (coordinator) {
    coordinator.reSync();
  } else {
    pendingDiarySchedules.add(userId);
  }
}

export async function flushDiaryReplicationAndWait(
  userId: string,
): Promise<void> {
  const coordinator = diaryCoordinators.get(userId);
  if (coordinator) {
    await coordinator.flush();
    return;
  }

  pendingDiarySchedules.delete(userId);
  const db = await getDb(userId);
  const transientCoordinator = new DiaryReplicationCoordinator(db, userId);
  diaryCoordinators.set(userId, transientCoordinator);
  try {
    await transientCoordinator.flush();
  } finally {
    if (diaryCoordinators.get(userId) === transientCoordinator) {
      diaryCoordinators.delete(userId);
    }
    await transientCoordinator.cancel();
  }
}

function startDiaryDaysReplication(
  db: AppDatabase,
  userId: string,
): DiaryReplicationCoordinator {
  const coordinator = new DiaryReplicationCoordinator(db, userId);
  diaryCoordinators.set(userId, coordinator);
  coordinator.reSync();
  return coordinator;
}

function startCollectionReplication<TDoc extends object, TCheckpoint extends object>(
  collection: RxCollection<TDoc>,
  collectionName: string,
  userId: string,
  _pullIntervalMs: number,
  pullOnly = false,
): RxReplicationState<TDoc, TCheckpoint> {
  void _pullIntervalMs;
  type Doc = TDoc & { _deleted: boolean };

  return replicateRxCollection<TDoc, TCheckpoint>({
    collection,
    replicationIdentifier: `rxdb-${userId}-${collectionName}`,
    live: true,
    retryTime: 10_000,
    autoStart: true,

    pull: {
      batchSize: 100,
      handler: async (checkpoint, batchSize) => {
        return rxdbFetch<{ documents: Doc[]; checkpoint: TCheckpoint }>(
          `/api/rxdb/${collectionName}/pull`,
          { checkpoint, batchSize },
        );
      },
    },

    ...(pullOnly
      ? {}
      : {
          push: {
            batchSize: 50,
            handler: async (rows) => {
              return rxdbFetch<Doc[]>(`/api/rxdb/${collectionName}/push`, rows);
            },
          },
        }),
  });
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Start replication for the given user. Idempotent — safe to call multiple
 * times; only the first call per userId actually starts replication.
 */
export async function ensureReplication(userId: string): Promise<void> {
  const inFlight = startPromises.get(userId);
  if (inFlight) return inFlight;
  if (started.has(userId)) return;

  const startPromise = (async () => {
    const config = await syncConfigurationService.loadConfiguration();
    if (!config.enabled) return;

    // Mark as started optimistically; clear the flag if setup fails so the
    // next call can retry (e.g. after a transient IndexedDB open failure).
    started.add(userId);

    try {
      const db = await getDb(userId);
      const pullIntervalMs = (config.autoSync?.intervalMinutes ?? 5) * 60_000;
      const collMap = new Map<string, ReplicationStateHandle>();

      const diaryCoordinator = startDiaryDaysReplication(db, userId);
      collMap.set(DIARY_DAYS_COLLECTION, diaryCoordinator);
      if (pendingDiarySchedules.delete(userId)) {
        diaryCoordinator.schedule();
      }
      collMap.set(RECIPES_COLLECTION, startCollectionReplication<RecipeDocType, RecipesCheckpoint>(
        db[RECIPES_COLLECTION], RECIPES_COLLECTION, userId, pullIntervalMs,
      ));
      collMap.set(USER_FOODS_COLLECTION, startCollectionReplication<UserFoodDocType, UserFoodsCheckpoint>(
        db[USER_FOODS_COLLECTION], USER_FOODS_COLLECTION, userId, pullIntervalMs,
      ));
      collMap.set(GOALS_HISTORY_COLLECTION, startCollectionReplication<GoalHistoryDocType, GoalsHistoryCheckpoint>(
        db[GOALS_HISTORY_COLLECTION], GOALS_HISTORY_COLLECTION, userId, pullIntervalMs,
      ));
      collMap.set(WEIGHT_HISTORY_COLLECTION, startCollectionReplication<WeightHistoryDocType, WeightHistoryCheckpoint>(
        db[WEIGHT_HISTORY_COLLECTION], WEIGHT_HISTORY_COLLECTION, userId, pullIntervalMs,
      ));
      collMap.set(MEALS_COLLECTION, startCollectionReplication<MealsConfigurationDocType, MealsCheckpoint>(
        db[MEALS_COLLECTION], MEALS_COLLECTION, userId, pullIntervalMs,
      ));

      collMap.set(CUSTOM_FOOD_MIGRATIONS_COLLECTION,
        startCollectionReplication<CustomFoodMigrationDocType, CustomFoodMigrationsCheckpoint>(
          db[CUSTOM_FOOD_MIGRATIONS_COLLECTION], CUSTOM_FOOD_MIGRATIONS_COLLECTION, userId, pullIntervalMs, true,
        ),
      );
      collMap.set(SETTINGS_COLLECTION,
        startCollectionReplication<SettingDocType, SettingsCheckpoint>(
          db[SETTINGS_COLLECTION], SETTINGS_COLLECTION, userId, pullIntervalMs,
        ),
      );

      statesMap.set(userId, collMap);
    } catch (err) {
      // Allow future calls to retry after a transient failure.
      started.delete(userId);
      await cancelAll(userId);
      throw err;
    }
  })();

  startPromises.set(userId, startPromise);
  try {
    await startPromise;
  } finally {
    if (startPromises.get(userId) === startPromise) {
      startPromises.delete(userId);
    }
  }
}

/**
 * Collections whose initial pull gates first-launch routing decisions.
 * `settings` holds the `onboarding:completed` flag; `goals-history` lets the
 * onboarding guard auto-complete for a returning user. These must be present
 * locally before the guard decides whether to send the user into onboarding.
 */
const ROUTING_CRITICAL_COLLECTIONS = [
  SETTINGS_COLLECTION,
  GOALS_HISTORY_COLLECTION,
];

/**
 * Ensure replication is running and wait for the first successful pull of the
 * routing-critical collections (settings + goals-history), so that on a fresh
 * login on a new device the server's onboarding state is present locally before
 * the onboarding guard runs.
 *
 * Bounded by `timeoutMs`: `awaitInitialReplication()` only resolves after a
 * *successful* pull, so for a genuinely-new or offline user (whose first-ever
 * pull can't complete) it would never resolve. The timeout guarantees we fall
 * through instead of soft-locking on the syncing screen — the onboarding guard
 * then runs against local data and correctly sends a brand-new user into
 * onboarding. Live replication continues in the background either way.
 *
 * @returns `true` if the initial pull completed, `false` if the timeout fired.
 */
export async function awaitInitialSync(
  userId: string,
  timeoutMs = 10_000,
): Promise<boolean> {
  await ensureReplication(userId);
  const collMap = statesMap.get(userId);
  // Sync disabled (or no collections registered) — nothing to wait for.
  if (!collMap) return true;

  const initial = ROUTING_CRITICAL_COLLECTIONS
    .map((name) => collMap.get(name)?.awaitInitialReplication?.())
    .filter((p): p is Promise<void> => !!p)
    // Swallow post-timeout rejections (e.g. replication cancelled) so they
    // never surface as unhandled rejections after the race resolves.
    .map((p) => p.then(() => undefined, () => undefined));
  if (initial.length === 0) return true;

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => resolve(false), timeoutMs);
  });
  try {
    return await Promise.race([
      Promise.all(initial).then(() => true),
      timedOut,
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Stop all replication for the given user (call on logout).
 */
export async function stopReplication(userId: string): Promise<void> {
  started.delete(userId);
  await cancelAll(userId);
}
